import axios from 'axios';
import { SERVER } from '../../Constants';

const apiClient = axios.create({
    baseURL: SERVER + '/faqs',
    withCredentials: true
});

/**
 * Adds a new FAQ.
 * @param {string} language - Language code (e.g., 'en', 'ar').
 * @param {string} question - The FAQ question.
 * @param {string} response - The FAQ answer.
 * @returns {Promise<object>} - The created FAQ data.
 */
export const postFAQ = async (language, question, response) => {
    try {
        const res = await apiClient.post('/', { language, question, response });
        return res.data;
    } catch (error) {
        console.error('postFAQ error:', error.response);
        throw new Error(error.response?.data?.message || 'Failed to add FAQ');
    }
};

/**
 * Fetches all FAQs for managers (all languages and statuses).
 * @returns {Promise<object>} - The full FAQ list.
 */
export const getAllFAQs = async () => {
    try {
        const res = await apiClient.get('/all');
        return res.data;
    } catch (error) {
        console.error('getAllFAQs error:', error.response);
        throw new Error(error.response?.data?.message || 'Failed to fetch FAQs');
    }
};

/**
 * Fetches a specific FAQ by ID.
 * @param {string} id - FAQ ID.
 * @returns {Promise<object>} - The FAQ object.
 */
export const getFAQById = async (id) => {
    try {
        const res = await apiClient.get(`/${id}`);
        return res.data;
    } catch (error) {
        console.error('getFAQById error:', error.response);
        throw new Error(error.response?.data?.message || 'Failed to fetch FAQ');
    }
};

/**
 * Updates a specific FAQ.
 * @param {string} id - The ID of the FAQ to update.
 * @param {object} updateData - Object containing updated fields (question, response, language, isActive).
 * @returns {Promise<object>} - The updated FAQ data.
 */
export const updateFAQ = async (id, updateData) => {
    try {
        const res = await apiClient.put(`/${id}`, updateData);
        return res.data;
    } catch (error) {
        console.error('updateFAQ error:', error.response);
        throw new Error(error.response?.data?.message || 'Failed to update FAQ');
    }
};

/**
 * Deletes a specific FAQ.
 * @param {string} id - FAQ ID to delete.
 * @returns {Promise<object>} - Response message.
 */
export const deleteFAQ = async (id) => {
    try {
        const res = await apiClient.delete(`/${id}`);
        return res.data;
    } catch (error) {
        console.error('deleteFAQ error:', error.response);
        throw new Error(error.response?.data?.message || 'Failed to delete FAQ');
    }
};
